import { Overlay, OverlayConfig, OverlayPositionBuilder, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ElementRef, Injectable, Injector, OnDestroy } from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';

import { Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

import { CatalogEntry } from '@apps/its2/models';

import { PopupTreeviewRef } from './popup-treeview-ref';
import { TreeModel } from '../../treeview';
import { PopupTreeviewComponent } from './popup-treeview/popup-treeview.component';

@Injectable()
export class PopupTreeviewService implements OnDestroy
{
    public overlayRef: OverlayRef = null;
    public selectedItem = new Subject<CatalogEntry>();
    public clickedOutside = new Subject();
    private destroyator = new Subject();
    private elementRef: ElementRef;
    private width: number;
    private height: number;
    private injector: Injector;

    constructor (
        private overlay: Overlay,
        protected overlayPositionBuilder: OverlayPositionBuilder
    )
    { }

    ngOnDestroy ()
    {
        this.destroy();
        this.destroyator.next(null);
        this.destroyator.complete();
        this.selectedItem.complete();
    }

    create (source: ElementRef, treeModel: TreeModel, selectedEntry: CatalogEntry, width: number, height: number): void
    {
        this.elementRef = source;
        this.width = width;
        this.height = height;

        this.overlayRef = this.overlay.create(this.getOverlayConfig());

        this.injector = Injector.create({
            providers: [
                {
                    provide: PopupTreeviewRef,
                    useFactory: () =>
                        new PopupTreeviewRef(this.overlayRef)
                }
            ]
        });
        const component = this.overlayRef.attach(new ComponentPortal(PopupTreeviewComponent, null, this.injector));
        // @Input()
        component.instance.treeModel = treeModel;
        component.instance.catalogEntry = selectedEntry;
        component.instance.width = width;
        component.instance.height = height;

        // @Output()
        component.instance.itemSelected
            .pipe(
                tap((catalogEntry: CatalogEntry) =>
                {
                    this.selectedItem.next(catalogEntry);
                    this.overlayRef.dispose();
                }),
                takeUntil(this.destroyator)
            )
            .subscribe();
        component.instance.clickedOutside
            .pipe(
                filter((clickedOutside: boolean) =>
                    clickedOutside
                ),
                tap(() =>
                {
                    this.clickedOutside.next(null);
                    this.clickedOutside.complete();
                    this.overlayRef.dispose();
                }
                ),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    public destroy ()
    {
        if (this.injector)
        {
            this.overlayRef.dispose();
        }
    }

    private getOverlayConfig (): OverlayConfig
    {
        return new OverlayConfig({
            width: this.width,
            height: this.height,
            minWidth: this.width,
            minHeight: this.height,
            hasBackdrop: false,
            backdropClass: 'popup-treeview-backdrop',
            positionStrategy: this.getOverlayPosition()
        });
    }

    private getOverlayPosition (): PositionStrategy
    {
        return this.overlayPositionBuilder
            .flexibleConnectedTo(this.elementRef)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: -38,
                    offsetX: 0
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                    offsetY: 2,
                    offsetX: 0
                }
            ])
            .withPush(false);
    }
}
